<script lang="ts">
import type { Role, Task, TaskGenerator } from '../../../compliance-shared/types/compliance';
import TaskGeneratorDeleteModal from './TaskGeneratorDeleteModal.vue';
import { modalRegister } from '@/services/modals';

export async function taskGeneratorDeleteModal(
  taskGenerator: TaskGenerator,
  tasks: Task[],
  roles: Role[] | null,
): Promise<{ delete: boolean; tasksIncluded: { task: Task; include: boolean }[] }> {
  return modalRegister({
    content: TaskGeneratorDeleteModal,
    bindings: { taskGenerator, tasks, roles },
  });
}
</script>

<script setup lang="ts">
import BootstrapModal from '@/components/modals/BootstrapModal.vue';
import TaskSmallCard from '@/components/tasks/TaskSmallCard.vue';
import { ref, toRef, watch } from 'vue';

const props = defineProps<{
  taskGenerator: TaskGenerator;
  tasks: Task[];
  roles: Role[] | null;
}>();

const modal = ref<InstanceType<typeof BootstrapModal> | null>(null);
const tasksIncluded = ref<{ task: Task; include: boolean }[]>([]);

watch(
  toRef(props, 'tasks'),
  function (tasks) {
    tasksIncluded.value = tasks.map(function (task) {
      return {
        task,
        include: false,
      };
    });
  },
  { immediate: true },
);

defineExpose({ modal });
</script>
<template>
  <BootstrapModal
    ref="modal"
    :title="'Delete ' + taskGenerator.title"
    :defaultCloseValue="{ delete: false, tasksIncluded: [] }"
  >
    <template #default="{ close }">
      <template v-if="tasks.length > 0">
        <p>When deleting a task schedule, you can also delete any incomplete tasks if they are no longer applicable.</p>

        <p>Which of the following tasks do you want to delete?</p>

        <p class="hstack justify-content-start gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="tasksIncluded = tasksIncluded.map((t) => ({ ...t, include: true }))"
          >
            <i class="fa-light fa-trash"></i> Delete all
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="tasksIncluded = tasksIncluded.map((t) => ({ ...t, include: false }))"
          >
            <i class="fa-light fa-check-double"></i> Keep all
          </button>
        </p>
        <div class="vstack gap-1">
          <template v-for="task of tasksIncluded" :key="task.task.taskID">
            <div class="hstack align-items-start gap-1">
              <div>
                <select class="form-select" v-model="task.include" style="min-width: 100px">
                  <option :value="false">Keep</option>
                  <option :value="true">Delete</option>
                </select>
              </div>

              <TaskSmallCard class="flex-grow-1" :task="task.task" :roles="roles" />
            </div>
          </template>
        </div>

        <hr />
      </template>

      <div class="hstack justify-content-center gap-3">
        <button class="btn btn-danger" @click="close({ delete: true, tasksIncluded })">Delete</button>
        <button class="btn btn-secondary" @click="close({ delete: false, tasksIncluded: [] })">Cancel</button>
      </div>
    </template>
  </BootstrapModal>
</template>
